/*-----------------------------------------------------------------------------/
/ EXC sticky add to cart
/
/ Shows the bar once the real Add to cart button has scrolled off the top of
/ the screen, and hides it again when that button is back in view. An
/ IntersectionObserver on the buy buttons block does the watching, so there is
/ no scroll handler running on every frame.
/
/ "Off the top" matters: a button that has simply not been reached yet is also
/ out of view, and showing the bar then would put it on screen before the
/ shopper has seen the real one. The observer entry's boundingClientRect tells
/ the two apart.
/
/ The button does not rebuild the product form. It clicks the theme's own Add
/ to cart, so the selected variant, the cart drawer and anything else listening
/ to that button keep working. The price is cloned out of the theme's price
/ block and re-cloned when the variant changes.
/
/ Notes: hq/projects/excdesigns/theme-build-notes.md
/-----------------------------------------------------------------------------*/

class StickyAtc extends HTMLElement {
  connectedCallback() {
    // The bar is on the page. layout/theme.liquid prints this class on the
    // server; setting it here as well covers the theme editor, where the bar
    // can be switched on without a page load. exc-sticky-atc.css keys the
    // footer's extra room on it.
    document.body.classList.add('exc-has-sticky-atc');

    this.priceSlot = this.querySelector('[data-sticky-price]');
    this.button = this.querySelector('[data-sticky-add]');

    this.buyBlock =
      document.querySelector('.product-single__box__block--buy_buttons') || this.findButton();
    this.priceBlock = document.querySelector('.product-single__box__block--price');

    if (!this.findButton() || !this.buyBlock) return;

    this.syncPrice();
    this.watchPrice();
    this.watchScroll();

    this.button.addEventListener('click', () => {
      // Submit the real form rather than posting to /cart/add here, so the
      // theme's <product-form> runs and the cart drawer, the selected variant
      // and anything else listening behave exactly as they do up the page.
      // A synthetic click on the real button does not reach that handler, the
      // form's own submit event is what it listens for.
      // The form is looked up on every click: changing the variant replaces
      // the whole <product-form> (variant-select-components.js), so a form
      // kept from page load is a detached copy that submits nothing.
      const form = this.findForm();
      const realButton = this.findButton();
      if (!form || !realButton) return;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(realButton);
      } else {
        realButton.click();
      }
    });
  }

  findForm() {
    return (
      (this.buyBlock && this.buyBlock.querySelector && this.buyBlock.querySelector('form[action*="/cart/add"]')) ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  findButton() {
    const form = this.findForm();
    return form ? form.querySelector('[name="add"], button[type="submit"]') : null;
  }

  disconnectedCallback() {
    // Theme editor: the section can be re-rendered or the bar switched off.
    // Stop watching, and do not leave the corner buttons lifted for a bar that
    // is gone. A bar that comes back writes its state again.
    if (this.observer) this.observer.disconnect();
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    this.observer = null;
    this.onScroll = null;
    this.visibleState = undefined;
    document.body.classList.remove('exc-sticky-atc-visible');
  }

  show(on) {
    if (!this.isConnected) return;
    // Skip the write entirely when the state has not changed
    if (this.visibleState === on) return;
    this.visibleState = on;
    this.dataset.visible = on ? 'true' : 'false';
    this.setAttribute('aria-hidden', on ? 'false' : 'true');
    // The same switch as a class on <body>. exc-floating-cart.css and
    // exc-sticky-atc.css use it to lift the corner buttons above the bar. It
    // replaced body:has() rules, which made the browser re-check the whole page
    // after every change. data-visible stays: other code reads it.
    document.body.classList.toggle('exc-sticky-atc-visible', on);
  }

  update() {
    // Read the button's position now rather than trusting a queued record: a
    // callback can arrive holding several, and the first of them is the
    // oldest, which during a fast scroll describes where the button used to be.
    this.show(this.buyBlock.getBoundingClientRect().bottom <= 0);
  }

  watchScroll() {
    // Kept on the element so disconnectedCallback can stop both watchers
    this.observer = new IntersectionObserver(() => this.update(), { threshold: 0 });
    // Observing already fires once with the current state, no separate initial read needed
    this.observer.observe(this.buyBlock);

    // The observer only fires when the button crosses in or out of view. A jump
    // that skips straight over it, a scroll to top button, an anchor link, or
    // the browser restoring a scroll position on reload, leaves the button out
    // of view on both sides of the move, so the state never changes and the
    // observer stays quiet. This is the backstop for that, tied to animation
    // frames so it is one cheap read per painted frame at most.
    let queued = false;
    this.onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        this.update();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  syncPrice() {
    if (!this.priceSlot || !this.priceBlock) return;
    const price = this.priceBlock.querySelector('.price');
    if (!price) return;

    const copy = price.cloneNode(true);
    // The real one carries the ids the theme updates; strip them so there are
    // never two elements on the page answering to the same id.
    copy.removeAttribute('id');
    copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));

    this.priceSlot.replaceChildren(copy);
  }

  watchPrice() {
    if (!this.priceBlock) return;
    // The theme swaps the price block's contents when the variant changes
    const observer = new MutationObserver(() => this.syncPrice());
    observer.observe(this.priceBlock, { childList: true, subtree: true, characterData: true });
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
